import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createEditor, Transforms, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { PlusIcon } from '@heroicons/react/24/outline';
import { TriangleAlert } from 'lucide-react';
import { Portal as TooltipPortal } from '@radix-ui/react-tooltip';

import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@dashboard/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@dashboard/components/ui/tooltip';

import { loadSlateContent } from '@dashboard/lib/slate/load-slate-content';
import {
  findStaleFieldVariableIds,
  isStaleFieldVariableId,
} from '@dashboard/lib/slate/stale-field-variables';
import { cn } from '@dashboard/lib/utils';

import PipingTag from './components/PipingTag';
import { withVariables } from './plugins/withVariables';

const VARIABLE_NODE_TYPE = 'variable';
const CONDITION_NODE_TYPE = 'condition';

const STALE_VARIABLE_HINT =
  'This field was removed, so it will be blank. Remove it or pick another field.';

// Variables written through the API carry an id but no name.
const variableName = (variable, variables) =>
  variable?.name || variables?.find(option => option.id === variable?.id)?.name || variable?.id;

// Custom element renderer with dialog position context
const ElementWithContext = props => (
  <Element {...props} editor={props.editor} variables={props.variables} />
);

// Custom element renderer
const Element = ({ attributes, children, element, editor, variables }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const conditionRef = useRef(null);

  switch (element.type) {
    case VARIABLE_NODE_TYPE: {
      const name = variableName(element.data, variables);
      if (!isStaleFieldVariableId(element.data.id, variables)) {
        return (
          <span {...attributes} contentEditable={false}>
            <PipingTag>{name}</PipingTag>
            {children}
          </span>
        );
      }
      return (
        <span {...attributes} contentEditable={false}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span aria-label={`${name}: ${STALE_VARIABLE_HINT}`}>
                  <PipingTag stale>{name}</PipingTag>
                </span>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>{STALE_VARIABLE_HINT}</TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
          {children}
        </span>
      );
    }
    case CONDITION_NODE_TYPE: {
      const isStale = isStaleFieldVariableId(element.data.variable?.id, variables);
      return (
        <div
          {...attributes}
          className="my-2 pl-4 border-l-2 border-blue-300 dark:border-blue-500/30"
        >
          <div
            ref={conditionRef}
            contentEditable={false}
            className={cn(
              'rounded-md text-xs px-2 py-1 cursor-pointer',
              isStale
                ? 'flex items-center gap-1 bg-red-50 dark:bg-red-500/15 text-destructive border border-destructive'
                : 'bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/15',
            )}
            data-stale={isStale ? 'true' : undefined}
            title={isStale ? STALE_VARIABLE_HINT : undefined}
            onClick={e => {
              e.preventDefault();
              setShowEditDialog(true);
            }}
          >
            {isStale && <TriangleAlert className="size-3 shrink-0" aria-hidden="true" />}
            if {variableName(element.data.variable, variables)} {element.data.operator}{' '}
            {element.data.value}
          </div>
          <div>{children}</div>

          {variables && (
            <EditConditionDialog
              isOpen={showEditDialog}
              onClose={() => setShowEditDialog(false)}
              initialVariable={element.data.variable}
              initialOperator={element.data.operator}
              initialValue={element.data.value}
              variables={variables}
              onSave={(variable, operator, value) => {
                const path = ReactEditor.findPath(editor, element);
                Transforms.setNodes(
                  editor,
                  {
                    data: { variable, operator, value },
                  },
                  { at: path },
                );
                setShowEditDialog(false);
                ReactEditor.focus(editor);
              }}
            />
          )}
        </div>
      );
    }
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// Custom leaf renderer
const Leaf = ({ attributes, children }) => {
  return <span {...attributes}>{children}</span>;
};

function getSlateValue(value) {
  try {
    const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
    return loadSlateContent(parsedValue);
  } catch (e) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
}

const RichTextarea = ({
  values,
  name,
  id,
  setFieldValue,
  variables = [],
  appearance = 'default',
  placeholder,
}) => {
  const editor = useMemo(() => withVariables(withHistory(withReact(createEditor()))), []);
  const [value, setValue] = useState(getSlateValue(values[name]));

  const editorRef = useRef(null);
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [showConditionDialog, setShowConditionDialog] = useState(false);
  const hasStaleFieldVariables = findStaleFieldVariableIds(value, variables).length > 0;

  const onKeyDown = useCallback(
    event => {
      if (event.key === '{' && variables.length > 0) {
        event.preventDefault();
        setShowVariableMenu(true);
      }
    },
    [variables],
  );

  const insertVariable = useCallback(
    variable => {
      const variableNode = {
        type: VARIABLE_NODE_TYPE,
        data: { name: variable.name, id: variable.id },
        children: [{ text: '' }], // must have a text child
      };

      // Insert the variable node inline at the cursor.
      Transforms.insertNodes(editor, variableNode);

      // 2) Insert a small text node after it so there's a place for the cursor
      Transforms.insertNodes(editor, { text: ' ' });

      // 3) Move the selection to the node we just inserted
      // Editor.after(...) finds the next valid position in the document after our insertion
      const { selection } = editor;
      if (selection) {
        const after = Editor.after(editor, selection, { unit: 'offset' });
        if (after) {
          Transforms.select(editor, after); // place cursor there
        }
      }

      // 4) Focus the editor so we can continue typing right after the variable
      ReactEditor.focus(editor);
    },
    [editor],
  );

  const focusEditor = useCallback(() => {
    setTimeout(() => {
      if (!editor.selection) {
        Transforms.select(editor, Editor.end(editor, []));
      }
      ReactEditor.focus(editor);
    }, 0);
  }, [editor]);

  // Tabbing in focuses the editable without a caret, and Slate drops keystrokes until one exists.
  const onFocus = useCallback(() => {
    if (!editor.selection) {
      Transforms.select(editor, Editor.end(editor, []));
    }
  }, [editor]);

  function onSelectVariable(variable) {
    insertVariable(variable);
    setShowVariableMenu(false);
  }

  function onChange(newValue) {
    setValue(newValue);
    setFieldValue(name, JSON.stringify(newValue));
  }

  const insertCondition = useCallback(
    (variable, operator, value) => {
      const conditionNode = {
        type: CONDITION_NODE_TYPE,
        data: {
          variable,
          operator,
          value,
        },
        children: [
          {
            type: 'paragraph',
            children: [{ text: '' }],
          },
        ],
      };

      Transforms.insertNodes(editor, conditionNode);
      const path = [...editor.selection.anchor.path.slice(0, -1), 0, 0];
      Transforms.select(editor, { path, offset: 0 });
      ReactEditor.focus(editor);
    },
    [editor],
  );

  return (
    <div style={{ position: 'relative' }}>
      {hasStaleFieldVariables && (
        <Alert variant="destructive" className="mb-2">
          <TriangleAlert />
          <AlertDescription>
            Some variables point to fields that were removed, so they will be blank. Remove them or
            pick another field.
          </AlertDescription>
        </Alert>
      )}
      <Slate editor={editor} initialValue={value} onChange={onChange}>
        <Editable
          ref={editorRef}
          className="overflow-auto block w-full px-3 py-2 text-sm rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
          // slate-react sets an inline min-height to the placeholder's height, which beats a class.
          style={{ minHeight: appearance === 'small' ? '4rem' : '8rem' }}
          id={id}
          placeholder={placeholder}
          renderPlaceholder={({ attributes, children }) => (
            // Slate pins the placeholder to the top edge and gives it width: 100%, which ignores
            // the padding on both axes; the width overflow shows up as a horizontal scrollbar.
            <span
              {...attributes}
              style={{
                ...attributes.style,
                top: undefined,
                width: 'auto',
                left: '0.75rem',
                right: '0.75rem',
                opacity: 1,
              }}
              className="text-muted-foreground"
            >
              {children}
            </span>
          )}
          renderElement={props => (
            <ElementWithContext {...props} editor={editor} variables={variables} />
          )}
          renderLeaf={Leaf}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
        />
      </Slate>

      {variables.length > 0 && (
        <div className="absolute bottom-2 right-2 flex space-x-2">
          <DropdownMenu open={showVariableMenu} onOpenChange={setShowVariableMenu}>
            <DropdownMenuTrigger asChild>
              <Button type="button" className="text-sm" size="small" appearance="secondary">
                <PlusIcon className="w-4 h-4 mr-2" /> Add Variable
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="top"
              className="max-h-64 w-56 overflow-y-auto"
              onCloseAutoFocus={event => {
                event.preventDefault();
                focusEditor();
              }}
            >
              {variables.map((variable, index) => (
                <DropdownMenuItem
                  key={`${variable.id}-${index}`}
                  onSelect={() => onSelectVariable(variable)}
                >
                  {variable.hint ? (
                    <div className="flex flex-col">
                      <span>{variable.name}</span>
                      <span className="text-xs text-muted-foreground">{variable.hint}</span>
                    </div>
                  ) : (
                    variable.name
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <EditConditionDialog
        isOpen={showConditionDialog}
        onClose={() => {
          setShowConditionDialog(false);
        }}
        onSave={(variable, operator, value) => {
          insertCondition(variable, operator, value);
          setShowConditionDialog(false);
        }}
        variables={variables}
      />
    </div>
  );
};

// EditConditionDialog component
const EditConditionDialog = ({
  isOpen,
  onClose,
  onSave,
  initialVariable,
  initialOperator = 'equals',
  initialValue = '',
  variables = [],
}) => {
  const [selectedVariable, setSelectedVariable] = useState(() => initialVariable || variables[0]);
  const [operator, setOperator] = useState(initialOperator);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (isOpen && variables.length > 0) {
      setSelectedVariable(initialVariable || variables[0]);
      setOperator(initialOperator);
      setValue(initialValue);
    }
  }, [isOpen, initialVariable, initialOperator, initialValue, variables]);

  const operators = [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'contains', value: 'contains' },
    { label: 'not contains', value: 'not_contains' },
    { label: 'starts with', value: 'starts_with' },
    { label: 'ends with', value: 'ends_with' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'less than', value: 'less_than' },
    { label: 'greater than or equal', value: 'greater_than_or_equal' },
    { label: 'less than or equal', value: 'less_than_or_equal' },
  ];

  return (
    <Dialog
      title="Add Condition"
      description="Add a condition to show or hide content based on variable values."
      open={isOpen}
      onCancel={onClose}
      confirmButton={{
        label: 'Save',
        onClick: () => onSave(selectedVariable, operator, value),
        appearance: 'primary',
      }}
      cancelButton={{
        label: 'Cancel',
        appearance: 'secondary',
      }}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-muted-foreground">
            Variable
          </label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-border shadow-sm"
            value={selectedVariable?.id}
            onChange={e => setSelectedVariable(variables.find(v => v.id === e.target.value))}
          >
            {variables.map(v => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-muted-foreground">
            Operator
          </label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-border shadow-sm"
            value={operator}
            onChange={e => setOperator(e.target.value)}
          >
            {operators.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-muted-foreground">
            Value
          </label>
          <input
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-border shadow-sm"
            value={value}
            onChange={e => setValue(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default RichTextarea;
